import React, { memo } from 'react';
import PropTypes from 'prop-types';
import VuiBox from 'components/ui/VuiBox';
import VuiTypography from 'components/ui/VuiTypography';
import { IoInformationCircleOutline, IoWarningOutline, IoFileTrayOutline, IoDocumentTextOutline } from 'react-icons/io5';

const GENERIC_ERROR_MESSAGE = 'Please check your connection and try again.';
const GENERIC_ERROR_TITLE = 'Something went wrong';
const USER_FRIENDLY_ICON_COLOR = '#f59e0b'; // Amber / warning tone
const INFO_COLOR = '#14b8a6'; // Teal color for noData

function looksTechnical(str) {
  if (!str || typeof str !== 'string') return false;
  const lower = str.toLowerCase();
  return (
    lower.startsWith('failed to') ||
    lower.startsWith('error') ||
    lower.startsWith('could not') ||
    lower.startsWith('unable to') ||
    lower.includes('data unavailable') ||
    lower.includes('please try again')
  );
}

/**
 * A standardized component for displaying messages like errors, no data, or informational text.
 */
const StandardMessageDisplay = memo(function StandardMessageDisplay({ type, title, message, containerHeight, iconSize, userFriendly }) {
  let IconComponent;
  let iconColor = 'white'; // Default icon color from theme.white.main
  let titleColor = 'white';
  let messageColor = 'text'; // Default for VuiTypography, maps to theme.text.main (#a0aec0)
  let displayTitle = title;
  let displayMessage = message;

  switch (type) {
    case 'error':
      IconComponent = IoWarningOutline;
      if (userFriendly !== false) {
        iconColor = USER_FRIENDLY_ICON_COLOR;
        titleColor = 'warning';
        messageColor = 'text';
        if (!message || looksTechnical(message)) displayMessage = GENERIC_ERROR_MESSAGE;
        if (!title || looksTechnical(title)) displayTitle = GENERIC_ERROR_TITLE;
      } else {
        iconColor = '#e31a1a';
        titleColor = 'error';
        messageColor = 'error';
        if (!message) displayMessage = GENERIC_ERROR_MESSAGE;
        if (!title) displayTitle = GENERIC_ERROR_TITLE;
      }
      break;
    case 'noData':
      IconComponent = IoDocumentTextOutline;
      iconColor = INFO_COLOR;
      titleColor = INFO_COLOR;
      break;
    case 'info':
    default:
      IconComponent = IoInformationCircleOutline;
      break;
  }

  return (
    <VuiBox
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      textAlign="center"
      sx={{ height: containerHeight || '100%', minHeight: containerHeight || 150, p: 2 }} // Padding for text
    >
      {IconComponent && <IconComponent size={iconSize} color={iconColor} style={{ marginBottom: '12px' }} />}
      {displayTitle && (
        <VuiTypography 
          variant="h6" 
          color={titleColor} 
          fontWeight="regular" 
          sx={{ mb: 1, color: titleColor === INFO_COLOR ? INFO_COLOR : undefined }}
        >
          {displayTitle}
        </VuiTypography>
      )}
      <VuiTypography variant="body2" color={messageColor} sx={{ maxWidth: '90%' }}>
        {displayMessage}
      </VuiTypography>
    </VuiBox>
  );
});

StandardMessageDisplay.propTypes = {
  /**
   * The type of message, determining icon and colors.
   */
  type: PropTypes.oneOf(['error', 'noData', 'info']).isRequired,
  /**
   * An optional title for the message.
   */
  title: PropTypes.string,
  /**
   * The main message text.
   */
  message: PropTypes.string,
  /**
   * The height of the container VuiBox.
   * Defaults to '100%' or a minHeight of 150px.
   */
  containerHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * Size of the icon.
   */
  iconSize: PropTypes.number,
  /**
   * When true (default for type="error"), uses softer colors and generic messages for technical errors.
   */
  userFriendly: PropTypes.bool,
};

StandardMessageDisplay.defaultProps = {
  title: null,
  containerHeight: null,
  iconSize: 32, // Default icon size from KeywordCloud
  userFriendly: true,
};

export default StandardMessageDisplay; 